/**
 * SupplyChainScene
 *
 * A hand-drawn cartoon world for the sign-in page: one little town where
 * the whole supply chain lives, left to right.
 *
 *   PLANNING  ·  demand signals drift in on bubbles → the HQ crunches them
 *                on a big monitor → a robot chews on the numbers
 *   EXECUTION ·  the factory makes it → the conveyor carries it out →
 *                the warehouse stores it → trucks run the road →
 *                a customer gets their parcel and waves
 *   …and a dashed loop carries the actuals back over the rooftops to HQ.
 *
 * Drawing notes (keep these if you edit the art):
 *   · Flat fills + one uniform ink outline (INK, 3 units). No gradients on
 *     objects — only the sky. That single rule is what makes it read as
 *     one illustration rather than a pile of clip art.
 *   · Everything sits on a ground plane: LOWER means NEARER. The conveyor,
 *     the robot and the forklift sit below the building bases on purpose,
 *     and the road is nearest of all.
 *   · Foliage, clouds and awnings are single closed paths, never stacks of
 *     circles — overlapping strokes would show their seams.
 *   · The palette is fixed rather than tokenised. It is a picture with its
 *     own sky, framed like a poster, so it should not re-tint with the app
 *     theme.
 *
 * Motion is all CSS keyframes; `prefers-reduced-motion` freezes the town
 * without hiding anything.
 */
import { cn } from "@/lib/utils";

/* Fixed illustration palette. */
const INK = "#33445c";
const C = {
  ink: INK,
  sky0: "#dbe7f2",
  sky1: "#f5f8fb",
  hillFar: "#dae4ed",
  grass: "#e1e9e3",
  grassFront: "#cddbd2",
  leaf: "#a8bfaf",
  leafHi: "#bccec2",
  trunk: "#a3907d",
  road: "#e9edf2",
  roadLine: "#c9b487",
  white: "#ffffff",
  glass: "#dde6f0",
  blue: "#3f63b8",
  teal: "#4f8f88",
  violet: "#6f6a99",
  gold: "#c8a566",
  coral: "#c78477",
  kraft: "#d7c2a4",
  kraftDk: "#bda98c",
  skin: "#cbb9a6",
  denim: "#6b7a91",
  dark: "#42536b",
  sun: "#efdcbb",
};

/* Paths shared between a shape and the things that travel along it. */
const ROAD =
  "M -40 650 C 110 624 240 614 380 622 C 512 630 570 662 700 656 C 822 650 900 628 1040 636";
const SIGNAL_PATH = "M 44 434 C 26 366 42 296 90 264 C 114 250 138 244 154 248";
const LOOP_PATH =
  "M 918 348 C 902 212 700 152 470 168 C 332 178 202 212 168 254";

/* ── Small reusable props ─────────────────────────────────────── */

const Cloud = ({
  x,
  y,
  s = 1,
  dur,
  delay,
}: {
  x: number;
  y: number;
  s?: number;
  dur: number;
  delay: number;
}) => (
  <g
    className="sc2-drift"
    style={{ animationDuration: `${dur}s`, animationDelay: `${delay}s` }}
  >
    <g transform={`translate(${x}, ${y}) scale(${s})`}>
      <path
        d="M 10 46 C -6 46 -6 22 12 20 C 12 2 44 -8 58 6 C 74 -6 98 4 96 20 C 112 22 112 46 96 46 Z"
        fill={C.white}
        opacity="0.95"
      />
    </g>
  </g>
);

/** Foliage is one closed blob so the outline never shows a seam. */
const Tree = ({ x, y, s = 1 }: { x: number; y: number; s?: number }) => (
  <g transform={`translate(${x}, ${y}) scale(${s})`}>
    <ellipse cx="0" cy="2" rx="26" ry="7" fill={INK} opacity="0.12" />
    <rect
      x="-5"
      y="-34"
      width="10"
      height="36"
      rx="4"
      fill={C.trunk}
      stroke={INK}
      strokeWidth="2.4"
    />
    <g className="sc2-sway">
      <path
        d="M -30 -50 C -34 -72 -14 -86 2 -78 C 16 -92 42 -80 38 -60 C 50 -46 36 -26 14 -30 C -4 -20 -28 -32 -30 -50 Z"
        fill={C.leaf}
        stroke={INK}
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path
        d="M -16 -62 C -18 -72 -6 -78 2 -73 C 8 -78 18 -74 17 -66 C 12 -60 -8 -56 -16 -62 Z"
        fill={C.leafHi}
      />
    </g>
  </g>
);

const Bush = ({ x, y, s = 1 }: { x: number; y: number; s?: number }) => (
  <g transform={`translate(${x}, ${y}) scale(${s})`}>
    <path
      d="M -24 0 C -30 -8 -26 -20 -14 -21 C -12 -32 4 -34 10 -25 C 22 -29 32 -18 26 -7 C 24 -2 20 0 16 0 Z"
      fill={C.leaf}
      stroke={INK}
      strokeWidth="2.4"
      strokeLinejoin="round"
    />
  </g>
);

/** Cardboard parcel; origin is its bottom-left corner. */
const Parcel = ({
  x,
  y,
  w = 26,
  h = 22,
}: {
  x: number;
  y: number;
  w?: number;
  h?: number;
}) => (
  <g transform={`translate(${x}, ${y - h})`}>
    <rect
      width={w}
      height={h}
      rx="3"
      fill={C.kraft}
      stroke={INK}
      strokeWidth="2.1"
    />
    <path
      d={`M 0 ${h * 0.38} H ${w}`}
      stroke={INK}
      strokeWidth="2"
      opacity="0.75"
    />
    <path
      d={`M ${w / 2} 0 V ${h * 0.38}`}
      stroke={INK}
      strokeWidth="2"
      opacity="0.75"
    />
    <rect
      x={w * 0.34}
      y={h * 0.38}
      width={w * 0.32}
      height={h * 0.62}
      fill={C.kraftDk}
      opacity="0.55"
    />
  </g>
);

/** A wheel that spins; spokes make the rotation readable. */
const Wheel = ({ cx, cy, r }: { cx: number; cy: number; r: number }) => (
  <g transform={`translate(${cx}, ${cy})`}>
    <circle r={r} fill={C.dark} stroke={INK} strokeWidth="2.1" />
    <g className="sc2-wheel">
      <circle r={r * 0.42} fill="#cfd8e8" />
      <path
        d={`M ${-r * 0.42} 0 H ${r * 0.42} M 0 ${-r * 0.42} V ${r * 0.42}`}
        stroke={INK}
        strokeWidth="1.5"
      />
    </g>
  </g>
);

/** Little townsfolk. Origin at the feet. */
const Person = ({
  x,
  y,
  s = 1,
  shirt,
  flip = false,
}: {
  x: number;
  y: number;
  s?: number;
  shirt: string;
  flip?: boolean;
}) => (
  <g transform={`translate(${x}, ${y}) scale(${flip ? -s : s}, ${s})`}>
    <ellipse cx="0" cy="2" rx="15" ry="4.5" fill={INK} opacity="0.12" />
    <rect x="-9" y="-24" width="7.5" height="24" rx="3.7" fill={C.denim} />
    <rect x="1.5" y="-24" width="7.5" height="24" rx="3.7" fill={C.denim} />
    <path
      d="M -13 -50 C -13 -56 13 -56 13 -50 L 15 -22 C 5 -18 -5 -18 -15 -22 Z"
      fill={shirt}
      stroke={INK}
      strokeWidth="2.25"
      strokeLinejoin="round"
    />
    <rect
      x="10"
      y="-49"
      width="7"
      height="26"
      rx="3.5"
      fill={shirt}
      stroke={INK}
      strokeWidth="2.1"
    />
    <circle cx="0" cy="-61" r="12" fill={C.skin} stroke={INK} strokeWidth="2.25" />
    <path
      d="M -12 -64 C -11 -75 11 -75 12 -64 C 7 -69 -6 -70 -12 -64 Z"
      fill={INK}
      opacity="0.7"
    />
  </g>
);

/** Signboard used on the buildings; width is caller-supplied. */
const Sign = ({
  x,
  y,
  w,
  h,
  fill,
  text,
  color = INK,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  fill: string;
  text: string;
  color?: string;
}) => (
  <g>
    <rect
      x={x}
      y={y}
      width={w}
      height={h}
      rx={h / 3.2}
      fill={fill}
      stroke={INK}
      strokeWidth="2.4"
    />
    <text
      x={x + w / 2}
      y={y + h / 2 + 5}
      textAnchor="middle"
      fill={color}
      fontSize="13"
      fontWeight="700"
      letterSpacing="1.5"
    >
      {text}
    </text>
  </g>
);

export interface SupplyChainSceneProps {
  className?: string;
}

export function SupplyChainScene({ className }: SupplyChainSceneProps) {
  return (
    <div
      className={cn("relative h-full w-full overflow-hidden select-none", className)}
      style={{ background: C.sky0 }}
    >
      {/* ── The town ──────────────────────────────────────────── */}
      <div className="absolute inset-0">
        <svg
          viewBox="0 -120 1000 820"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMax meet"
          className="absolute inset-x-0 bottom-0 block w-full h-auto"
          style={{ fontFamily: "Inter, system-ui, sans-serif" }}
          role="img"
          aria-label="Cartoon illustration of a supply chain town: demand signals float into a planning headquarters, a robot optimises the plan, a factory makes goods onto a conveyor, a warehouse stores them, trucks drive them down a winding road, and a customer receives a parcel — with a dashed loop carrying results back to the planners."
        >
          <defs>
            <linearGradient id="sc2-sky" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={C.sky0} />
              <stop offset="1" stopColor={C.sky1} />
            </linearGradient>
            <clipPath id="sc2-awning">
              <path d="M 866 424 H 970 L 976 446 H 860 Z" />
            </clipPath>
          </defs>

          <style>{`
            .sc2 text { paint-order: stroke; }

            @keyframes sc2-drift    { from { transform: translateX(-160px); } to { transform: translateX(1180px); } }
            @keyframes sc2-spin     { to { transform: rotate(360deg); } }
            @keyframes sc2-sunbeat  { 0%,100% { transform: scale(1); } 50% { transform: scale(1.06); } }
            @keyframes sc2-bar      { 0%,100% { transform: scaleY(.35); } 50% { transform: scaleY(1); } }
            @keyframes sc2-radar    { 0% { transform: scale(.3); opacity:.8; } 100% { transform: scale(2.6); opacity:0; } }
            @keyframes sc2-signal   { 0% { offset-distance: 0%; opacity:0; } 12% { opacity:1; } 82% { opacity:1; } 100% { offset-distance: 100%; opacity:0; } }
            @keyframes sc2-smoke    { 0% { transform: translate(0,0) scale(.45); opacity:.85; } 70% { opacity:.35; } 100% { transform: translate(-16px,-92px) scale(1.7); opacity:0; } }
            @keyframes sc2-march    { to { stroke-dashoffset: -32; } }
            @keyframes sc2-boxrun   { 0% { transform: translateX(0) translateY(0); opacity:0; }
                                      6% { opacity:1; }
                                      25% { transform: translateX(52px) translateY(-4px); }
                                      50% { transform: translateX(104px) translateY(0); }
                                      75% { transform: translateX(156px) translateY(-4px); }
                                      94% { opacity:1; }
                                      100% { transform: translateX(208px) translateY(0); opacity:0; } }
            @keyframes sc2-drive    { 0% { offset-distance: 0%; opacity: 0; } 5% { opacity:1; } 95% { opacity:1; } 100% { offset-distance: 100%; opacity:0; } }
            @keyframes sc2-shuttle  { 0%,8%   { transform: translateX(0); }
                                      42%,58% { transform: translateX(104px); }
                                      92%,100%{ transform: translateX(0); } }
            @keyframes sc2-hover    { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-26px,-14px); } }
            @keyframes sc2-rotor    { 0%,100% { transform: scaleX(1); } 50% { transform: scaleX(.18); } }
            @keyframes sc2-sway     { 0%,100% { transform: rotate(-1.6deg); } 50% { transform: rotate(1.6deg); } }
            @keyframes sc2-converge { 0% { stroke-dashoffset: 130; } 45%,100% { stroke-dashoffset: 0; } }
            @keyframes sc2-led      { 0%,100% { opacity: .22; } 50% { opacity: 1; } }
            @keyframes sc2-loopride { 0% { offset-distance: 0%; opacity:0; } 10% { opacity:1; } 88% { opacity:1; } 100% { offset-distance: 100%; opacity:0; } }

            .sc2-drift    { animation: sc2-drift linear infinite; }
            .sc2-sunbeat  { transform-box: fill-box; transform-origin: center; animation: sc2-sunbeat 9s ease-in-out infinite; }
            .sc2-bar      { transform-box: fill-box; transform-origin: bottom; animation: sc2-bar 3.6s ease-in-out infinite; }
            .sc2-radar    { transform-box: fill-box; transform-origin: center; animation: sc2-radar 4.2s ease-out infinite; }
            .sc2-signal   { offset-path: path('${SIGNAL_PATH}'); animation: sc2-signal 12s linear infinite; }
            .sc2-smoke    { animation: sc2-smoke 5.6s ease-out infinite; }
            .sc2-belt     { stroke-dasharray: 10 8; animation: sc2-march 1.7s linear infinite; }
            .sc2-roadline { stroke-dasharray: 20 18; animation: sc2-march 2.7s linear infinite; }
            .sc2-loopline { stroke-dasharray: 9 10; animation: sc2-march 3.4s linear infinite; }
            .sc2-boxrun   { animation: sc2-boxrun 9.5s linear infinite; }
            .sc2-wheel    { transform-box: fill-box; transform-origin: center; animation: sc2-spin 1.7s linear infinite; }
            .sc2-drive    { offset-path: path('${ROAD}'); offset-rotate: auto; animation: sc2-drive 24s linear infinite; }
            .sc2-shuttle  { animation: sc2-shuttle 11s ease-in-out infinite; }
            .sc2-hover    { animation: sc2-hover 9s ease-in-out infinite; }
            .sc2-rotor    { transform-box: fill-box; transform-origin: center; animation: sc2-rotor .3s linear infinite; }
            .sc2-sway     { transform-box: fill-box; transform-origin: bottom center; animation: sc2-sway 7.5s ease-in-out infinite; }
            .sc2-converge { stroke-dasharray: 130; animation: sc2-converge 7s ease-in-out infinite; }
            .sc2-led      { animation: sc2-led 2.8s ease-in-out infinite; }
            .sc2-loopride { offset-path: path('${LOOP_PATH}'); animation: sc2-loopride 15s linear infinite; }

            @media (prefers-reduced-motion: reduce) {
              .sc2 * { animation: none !important; }
              .sc2-signal, .sc2-drive, .sc2-loopride { offset-distance: 42% !important; opacity: 1 !important; }
            }
          `}</style>

          <g className="sc2">
            {/* ── Sky ──────────────────────────────────────────── */}
            <rect x="0" y="-120" width="1000" height="820" fill="url(#sc2-sky)" />

            {/* Soft daylight */}
            <g transform="translate(908, 132)">
              <circle r="86" fill={C.sun} opacity="0.16" className="sc2-sunbeat" />
              <circle r="54" fill={C.sun} opacity="0.22" />
              <circle r="30" fill={C.sun} opacity="0.3" />
            </g>

            {/* Clouds */}
            <Cloud x={-130} y={-104} s={0.8} dur={106} delay={-26} />
            <Cloud x={-130} y={-38} s={1.05} dur={92} delay={-70} />
            <Cloud x={-130} y={58} s={1.15} dur={78} delay={-12} />
            <Cloud x={-130} y={136} s={0.75} dur={96} delay={-58} />
            <Cloud x={-130} y={92} s={0.95} dur={112} delay={-84} />

            {/* ── Hills ────────────────────────────────────────── */}
            <path
              d="M -20 456 C 92 402 182 398 268 432 C 352 464 420 406 522 410 C 632 414 700 454 800 440 C 900 426 962 442 1020 430 L 1020 720 L -20 720 Z"
              fill={C.hillFar}
            />

            {/* ── Feedback loop over the rooftops ──────────────── */}
            <g>
              <path
                d={LOOP_PATH}
                fill="none"
                stroke={INK}
                strokeWidth="2.4"
                strokeLinecap="round"
                opacity="0.3"
                className="sc2-loopline"
              />
              {[0, 1, 2].map((i) => (
                <g
                  key={i}
                  className="sc2-loopride"
                  style={{ animationDelay: `${i * 3.6}s` }}
                >
                  <circle
                    r="15"
                    fill={C.white}
                    stroke={INK}
                    strokeWidth="2.1"
                  />
                  {i === 0 && (
                    <path
                      d="M -6 3 L -2 -3 L 2 1 L 6 -5"
                      fill="none"
                      stroke={C.teal}
                      strokeWidth="2.25"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  )}
                  {i === 1 && (
                    <path
                      d="M 0 -7 L 2 -2 L 7 -2 L 3 1 L 5 6 L 0 3 L -5 6 L -3 1 L -7 -2 L -2 -2 Z"
                      fill={C.gold}
                      stroke="none"
                    />
                  )}
                  {i === 2 && (
                    <g>
                      <rect
                        x="-6"
                        y="-1"
                        width="3.4"
                        height="7"
                        fill={C.blue}
                      />
                      <rect
                        x="-1.7"
                        y="-5"
                        width="3.4"
                        height="11"
                        fill={C.blue}
                      />
                      <rect
                        x="2.6"
                        y="-3"
                        width="3.4"
                        height="9"
                        fill={C.blue}
                      />
                    </g>
                  )}
                </g>
              ))}
            </g>

            {/* ── Ground ───────────────────────────────────────── */}
            <path
              d="M -20 508 C 120 474 244 490 366 504 C 506 520 604 480 722 490 C 842 500 932 478 1020 494 L 1020 720 L -20 720 Z"
              fill={C.grass}
            />

            {/* ── Demand signals drifting into HQ ──────────────── */}
            <g>
              {[
                { d: 0, glyph: "doc" },
                { d: 2.25, glyph: "tag" },
                { d: 4.5, glyph: "chart" },
                { d: 6.75, glyph: "cloud" },
              ].map(({ d, glyph }) => (
                <g
                  key={glyph}
                  className="sc2-signal"
                  style={{ animationDelay: `${d}s` }}
                >
                  <circle
                    r="17"
                    fill={C.white}
                    stroke={INK}
                    strokeWidth="2.25"
                  />
                  {glyph === "doc" && (
                    <g>
                      <rect
                        x="-6"
                        y="-8"
                        width="12"
                        height="16"
                        rx="1.8"
                        fill="#eef2f7"
                        stroke={INK}
                        strokeWidth="1.7"
                      />
                      <path
                        d="M -3 -4 H 3 M -3 0 H 3 M -3 4 H 1"
                        stroke={INK}
                        strokeWidth="1.45"
                        strokeLinecap="round"
                        opacity="0.6"
                      />
                    </g>
                  )}
                  {glyph === "tag" && (
                    <g transform="rotate(-20)">
                      <path
                        d="M -7 -6 H 3 L 8 0 L 1 7 L -7 3 Z"
                        fill={C.gold}
                        stroke={INK}
                        strokeWidth="1.7"
                        strokeLinejoin="round"
                      />
                      <circle cx="-2.5" cy="-1.5" r="1.8" fill={INK} />
                    </g>
                  )}
                  {glyph === "chart" && (
                    <g>
                      <rect x="-7" y="0" width="4" height="7" fill={C.blue} />
                      <rect x="-1.5" y="-5" width="4" height="12" fill={C.blue} />
                      <rect x="4" y="-2" width="4" height="9" fill={C.blue} />
                    </g>
                  )}
                  {glyph === "cloud" && (
                    <g>
                      <path
                        d="M -7 3 C -11 3 -11 -3 -6 -4 C -5 -9 3 -10 5 -5 C 10 -5 10 3 6 3 Z"
                        fill={C.glass}
                        stroke={INK}
                        strokeWidth="1.7"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M -3 6 V 9 M 3 6 V 9"
                        stroke={C.blue}
                        strokeWidth="1.85"
                        strokeLinecap="round"
                      />
                    </g>
                  )}
                </g>
              ))}
            </g>

            {/* ══ HQ — sense & forecast ════════════════════════ */}
            <g>
              <ellipse cx="155" cy="504" rx="96" ry="11" fill={INK} opacity="0.12" />
              {/* antenna + radar */}
              <line x1="155" y1="286" x2="155" y2="250" stroke={INK} strokeWidth="3.4" />
              {[0, 1, 2].map((i) => (
                <circle
                  key={i}
                  cx="155"
                  cy="248"
                  r="12"
                  fill="none"
                  stroke={C.blue}
                  strokeWidth="2.4"
                  className="sc2-radar"
                  style={{ animationDelay: `${i}s` }}
                />
              ))}
              <circle cx="155" cy="248" r="6.5" fill={C.gold} stroke={INK} strokeWidth="2.25" />
              {/* roof + body */}
              <path
                d="M 62 336 L 155 284 L 248 336 Z"
                fill={C.blue}
                stroke={INK}
                strokeWidth="2.4"
                strokeLinejoin="round"
              />
              <rect x="82" y="334" width="146" height="170" rx="10" fill={C.white} stroke={INK} strokeWidth="2.4" />
              {/* the big planning monitor */}
              <rect x="98" y="354" width="114" height="76" rx="9" fill={C.glass} stroke={INK} strokeWidth="2.4" />
              {[
                [110, C.blue, 0],
                [128, C.teal, 0.3],
                [146, C.gold, 0.6],
                [164, C.coral, 0.9],
              ].map(([bx, fill, dly], i) => (
                <rect
                  key={i}
                  x={bx as number}
                  y="382"
                  width="13"
                  height="38"
                  rx="2.5"
                  fill={fill as string}
                  className="sc2-bar"
                  style={{ animationDelay: `${dly}s` }}
                />
              ))}
              <path
                d="M 184 414 L 192 396 L 200 402"
                fill="none"
                stroke={C.coral}
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M 194 394 L 202 393 L 201 401 Z" fill={C.coral} />
              <line x1="104" y1="422" x2="206" y2="422" stroke={INK} strokeWidth="2" opacity="0.5" />
              {/* door + a planner heading out */}
              <rect x="142" y="452" width="34" height="52" rx="7" fill={C.gold} stroke={INK} strokeWidth="2.4" />
              <circle cx="169" cy="480" r="2.6" fill={INK} />
              <Person x={216} y={504} s={0.62} shirt={C.teal} />
            </g>
            {/* HQ nameplate */}
            <Sign x={96} y={510} w={118} h={30} fill={C.white} text="FORECAST" />

            {/* ══ Optimisation engine ═════════════════════════ */}
            <g>
              <ellipse cx="323" cy="520" rx="58" ry="9" fill={INK} opacity="0.12" />
              <rect x="272" y="506" width="102" height="11" rx="5.5" fill={C.dark} />
              <rect x="280" y="378" width="86" height="132" rx="12" fill={C.white} stroke={INK} strokeWidth="2.4" />
              <rect x="284" y="392" width="4" height="106" rx="2" fill={C.blue} opacity="0.7" />
              {/* screen: an optimisation run converging on its target */}
              <rect x="294" y="390" width="60" height="42" rx="6" fill={C.glass} stroke={INK} strokeWidth="2" />
              <line x1="298" y1="400" x2="350" y2="400" stroke={INK} strokeWidth="1.5" strokeDasharray="3 3" opacity="0.4" />
              <polyline
                className="sc2-converge"
                points="298,426 308,410 318,420 328,405 338,408 350,401"
                fill="none"
                stroke={C.blue}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* compute modules */}
              {[438, 462, 486].map((my, i) => (
                <g key={my}>
                  <rect x="294" y={my} width="60" height="20" rx="4" fill="#eef2f7" stroke={INK} strokeWidth="1.7" />
                  <line x1="300" y1={my + 10} x2="328" y2={my + 10} stroke={INK} strokeWidth="1.7" opacity="0.25" />
                  <circle
                    cx="339"
                    cy={my + 10}
                    r="2.8"
                    fill={C.teal}
                    className="sc2-led"
                    style={{ animationDelay: `${i * 0.6}s` }}
                  />
                  <circle
                    cx="347"
                    cy={my + 10}
                    r="2.8"
                    fill={C.gold}
                    className="sc2-led"
                    style={{ animationDelay: `${i * 0.6 + 0.3}s` }}
                  />
                </g>
              ))}
            </g>
            <Sign x={268} y={524} w={110} h={28} fill={C.white} text="OPTIMIZE" />

            {/* ══ Factory — make ═════════════════════════════ */}
            <g>
              <ellipse cx="516" cy="518" rx="128" ry="12" fill={INK} opacity="0.12" />
              {/* chimneys behind the roof */}
              <rect x="452" y="238" width="28" height="80" rx="5" fill={C.white} stroke={INK} strokeWidth="2.4" />
              <rect x="446" y="230" width="40" height="14" rx="6" fill={C.teal} stroke={INK} strokeWidth="2.4" />
              <rect x="550" y="214" width="28" height="104" rx="5" fill={C.white} stroke={INK} strokeWidth="2.4" />
              <rect x="544" y="206" width="40" height="14" rx="6" fill={C.teal} stroke={INK} strokeWidth="2.4" />
              {[
                [466, 224, 0],
                [466, 224, 1.4],
                [466, 224, 2.8],
                [564, 200, 0.7],
                [564, 200, 2.1],
                [564, 200, 3.5],
              ].map(([sx, sy, dly], i) => (
                <circle
                  key={i}
                  cx={sx}
                  cy={sy}
                  r="14"
                  fill={C.white}
                  stroke="#c3d1de"
                  strokeWidth="2.1"
                  className="sc2-smoke"
                  style={{ animationDelay: `${dly}s` }}
                />
              ))}
              {/* sawtooth roof */}
              <path
                d="M 396 358 L 396 336 L 436 304 L 436 336 L 476 304 L 476 336 L 516 304 L 516 336 L 556 304 L 556 336 L 596 304 L 596 336 L 636 306 L 636 358 Z"
                fill={C.teal}
                stroke={INK}
                strokeWidth="2.4"
                strokeLinejoin="round"
              />
              <rect x="402" y="356" width="228" height="162" rx="10" fill={C.white} stroke={INK} strokeWidth="2.4" />
              {[418, 472, 526, 580].map((wx) => (
                <g key={wx}>
                  <rect x={wx} y="376" width="36" height="30" rx="6" fill={C.glass} stroke={INK} strokeWidth="2.4" />
                  <path
                    d={`M ${wx + 18} 376 V 406 M ${wx} 391 H ${wx + 36}`}
                    stroke={INK}
                    strokeWidth="1.85"
                    opacity="0.55"
                  />
                </g>
              ))}
              {/* rolling shutter door */}
              <path
                d="M 482 518 L 482 476 A 34 34 0 0 1 550 476 L 550 518 Z"
                fill={C.glass}
                stroke={INK}
                strokeWidth="2.4"
                strokeLinejoin="round"
              />
              {[486, 496, 506].map((dy) => (
                <line key={dy} x1="484" y1={dy} x2="548" y2={dy} stroke={INK} strokeWidth="1.85" opacity="0.4" />
              ))}
              <Sign x={462} y={418} w={108} h={30} fill={C.gold} text="MAKE" />
            </g>

            {/* Conveyor running out of the factory (nearer than the building) */}
            <g>
              <rect x="418" y="556" width="18" height="26" rx="4" fill={C.dark} />
              <rect x="616" y="556" width="18" height="26" rx="4" fill={C.dark} />
              <rect x="410" y="538" width="232" height="22" rx="11" fill="#dde5f2" stroke={INK} strokeWidth="2.4" />
              <line
                x1="418"
                y1="541"
                x2="634"
                y2="541"
                stroke={INK}
                strokeWidth="2.1"
                opacity="0.45"
                className="sc2-belt"
              />
              {[432, 476, 520, 564, 608].map((rx) => (
                <g key={rx} transform={`translate(${rx}, 549)`}>
                  <circle r="7.5" fill={C.white} stroke={INK} strokeWidth="2" />
                  <g className="sc2-wheel">
                    <path d="M -5 0 H 5 M 0 -5 V 5" stroke={INK} strokeWidth="1.7" />
                  </g>
                </g>
              ))}
              {[0, 2.33, 4.66].map((dly, i) => (
                <g key={i} className="sc2-boxrun" style={{ animationDelay: `${dly}s` }}>
                  <Parcel x={418} y={538} w={30} h={26} />
                </g>
              ))}
              {/* a worker feeding the line */}
              <Person x={392} y={584} s={0.72} shirt={C.coral} />
            </g>

            {/* ══ Warehouse — store ══════════════════════════ */}
            <g>
              <ellipse cx="750" cy="514" rx="104" ry="11" fill={INK} opacity="0.12" />
              <path
                d="M 652 382 Q 750 320 848 382 Z"
                fill={C.gold}
                stroke={INK}
                strokeWidth="2.4"
                strokeLinejoin="round"
              />
              {[690, 750, 810].map((rx) => (
                <path
                  key={rx}
                  d={`M ${rx} 382 Q ${rx} 344 750 336`}
                  fill="none"
                  stroke={INK}
                  strokeWidth="1.7"
                  opacity="0.18"
                />
              ))}
              <rect x="662" y="380" width="176" height="134" rx="8" fill={C.white} stroke={INK} strokeWidth="2.4" />
              {/* open door with racking inside */}
              <rect x="700" y="428" width="100" height="86" rx="6" fill={C.dark} stroke={INK} strokeWidth="2.4" />
              {[452, 480].map((sy) => (
                <g key={sy}>
                  <line x1="706" y1={sy} x2="794" y2={sy} stroke="#5d6d8c" strokeWidth="4" strokeLinecap="round" />
                  <rect x="710" y={sy - 14} width="20" height="14" rx="2" fill={C.kraft} opacity="0.9" />
                  <rect x="736" y={sy - 16} width="22" height="16" rx="2" fill={C.kraftDk} opacity="0.9" />
                  <rect x="764" y={sy - 12} width="18" height="12" rx="2" fill={C.kraft} opacity="0.9" />
                </g>
              ))}
              <rect x="810" y="404" width="24" height="24" rx="5" fill={C.glass} stroke={INK} strokeWidth="2.25" />
              {/* hanging sign */}
              <line x1="716" y1="382" x2="716" y2="394" stroke={INK} strokeWidth="2" />
              <line x1="784" y1="382" x2="784" y2="394" stroke={INK} strokeWidth="2" />
              <Sign x={704} y={392} w={92} h={28} fill={C.white} text="STORE" />
              <Parcel x={666} y={514} w={30} h={24} />
              <Parcel x={670} y={490} w={22} h={20} />
            </g>

            {/* Forklift shuttling out of the warehouse */}
            <g className="sc2-shuttle">
              <g transform="translate(676, 574)">
                <ellipse cx="16" cy="4" rx="34" ry="6" fill={INK} opacity="0.13" />
                {/* mast + forks + pallet */}
                <rect x="36" y="-58" width="8" height="58" rx="3" fill={C.dark} stroke={INK} strokeWidth="2" />
                <path d="M 44 -8 H 62" stroke={INK} strokeWidth="4" strokeLinecap="round" />
                <Parcel x={44} y={-10} w={24} h={20} />
                {/* body */}
                <path
                  d="M -12 -8 L -12 -30 L 6 -30 L 10 -46 L 28 -46 L 28 -8 Z"
                  fill={C.coral}
                  stroke={INK}
                  strokeWidth="2.4"
                  strokeLinejoin="round"
                />
                <rect x="8" y="-42" width="16" height="14" rx="3" fill={C.glass} stroke={INK} strokeWidth="2" />
                <circle cx="16" cy="-52" r="7" fill={C.skin} stroke={INK} strokeWidth="2" />
                <Wheel cx={-4} cy={-1} r={9} />
                <Wheel cx={22} cy={-1} r={9} />
              </g>
            </g>

            {/* ══ Shop — deliver ═════════════════════════════ */}
            <g>
              <ellipse cx="918" cy="508" rx="76" ry="10" fill={INK} opacity="0.12" />
              <path
                d="M 850 396 L 918 348 L 986 396 Z"
                fill={C.coral}
                stroke={INK}
                strokeWidth="2.4"
                strokeLinejoin="round"
              />
              <rect x="864" y="394" width="108" height="114" rx="8" fill={C.white} stroke={INK} strokeWidth="2.4" />
              <Sign x={876} y={398} w={84} h={26} fill={C.white} text="DELIVER" />
              {/* striped awning */}
              <g>
                <path
                  d="M 866 424 H 970 L 976 446 H 860 Z"
                  fill={C.coral}
                  stroke={INK}
                  strokeWidth="2.4"
                  strokeLinejoin="round"
                />
                <g clipPath="url(#sc2-awning)">
                  {[879, 905, 931, 957].map((sx) => (
                    <rect key={sx} x={sx} y="420" width="13" height="34" fill={C.white} opacity="0.7" />
                  ))}
                </g>
              </g>
              <rect x="878" y="456" width="34" height="52" rx="6" fill={C.glass} stroke={INK} strokeWidth="2.4" />
              <circle cx="906" cy="482" r="2.6" fill={INK} />
              <rect x="926" y="456" width="36" height="32" rx="6" fill={C.glass} stroke={INK} strokeWidth="2.4" />
              {/* a customer collecting their order */}
              <Person x={848} y={556} s={0.82} shirt={C.blue} />
              <Parcel x={868} y={558} w={28} h={24} />
            </g>

            {/* ── Scenery filling the gaps ─────────────────────── */}
            <Tree x={240} y={512} s={0.84} />
            {/* far-left corner: a little kerbside still life */}
            <Parcel x={16} y={524} w={26} h={22} />
            <Parcel x={22} y={502} w={19} h={17} />
            <Tree x={860} y={496} s={0.66} />
            <Bush x={54} y={520} s={1.05} />
            <Bush x={648} y={516} s={0.85} />
            <Bush x={992} y={506} s={0.9} />

            {/* ── Delivery drone ───────────────────────────────── */}
            <g className="sc2-hover">
              <g transform="translate(656, 240)">
                <line x1="-26" y1="-4" x2="26" y2="-4" stroke={INK} strokeWidth="2.4" />
                {[-26, 26].map((ax) => (
                  <ellipse
                    key={ax}
                    cx={ax}
                    cy="-10"
                    rx="17"
                    ry="3.2"
                    fill={INK}
                    opacity="0.45"
                    className="sc2-rotor"
                  />
                ))}
                <rect x="-20" y="-6" width="40" height="18" rx="8" fill={C.blue} stroke={INK} strokeWidth="2.4" />
                <circle cx="10" cy="3" r="3.4" fill={C.white} />
                <line x1="-8" y1="12" x2="-8" y2="22" stroke={INK} strokeWidth="2" />
                <line x1="8" y1="12" x2="8" y2="22" stroke={INK} strokeWidth="2" />
                <Parcel x={-13} y={44} w={26} h={22} />
              </g>
            </g>

            {/* ── The road ─────────────────────────────────────── */}
            <path d={ROAD} fill="none" stroke={INK} strokeWidth="56" strokeLinecap="round" />
            <path d={ROAD} fill="none" stroke={C.road} strokeWidth="48" strokeLinecap="round" />
            <path
              d={ROAD}
              fill="none"
              stroke={C.roadLine}
              strokeWidth="4.5"
              strokeLinecap="round"
              className="sc2-roadline"
            />

            {/* Trucks running the road */}
            {[
              { delay: 0, box: C.blue, cab: C.white },
              { delay: -8.5, box: C.teal, cab: C.white },
            ].map(({ delay, box, cab }, i) => (
              <g key={i} className="sc2-drive" style={{ animationDelay: `${delay}s` }}>
                <g transform="translate(0, -14)">
                  <rect x="-40" y="-32" width="44" height="32" rx="5" fill={box} stroke={INK} strokeWidth="2.4" />
                  <rect x="-34" y="-26" width="14" height="12" rx="2.5" fill={C.white} opacity="0.75" />
                  <path
                    d="M 4 -32 L 18 -32 L 32 -14 L 32 0 L 4 0 Z"
                    fill={cab}
                    stroke={INK}
                    strokeWidth="2.4"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M 19 -28 L 28 -15 L 19 -15 Z"
                    fill={C.glass}
                    stroke={INK}
                    strokeWidth="1.85"
                    strokeLinejoin="round"
                  />
                  <Wheel cx={-26} cy={2} r={9.5} />
                  <Wheel cx={22} cy={2} r={9.5} />
                </g>
              </g>
            ))}

            {/* ── Foreground bank ──────────────────────────────── */}
            <path
              d="M -20 686 C 160 668 320 700 500 692 C 660 685 800 706 1020 684 L 1020 720 L -20 720 Z"
              fill={C.grassFront}
            />
            <Bush x={112} y={706} s={1.5} />
            <Bush x={598} y={712} s={1.8} />
            <Bush x={906} y={704} s={1.4} />
            {[40, 300, 430, 700, 830, 962].map((gx, i) => (
              <path
                key={gx}
                d={`M ${gx} 692 C ${gx - 5} 682 ${gx - 3} 676 ${gx} 672 C ${gx + 4} 678 ${gx + 6} 684 ${gx + 2} 692 Z`}
                fill={C.leaf}
                opacity={i % 2 ? 0.8 : 0.55}
              />
            ))}
          </g>

        </svg>
      </div>
    </div>
  );
}
